// client/src/coms/e2ee/ChannelEncryptionBar.js
// The encryption line at the top of a server channel. It only appears when there is something to act on or a warning
// (accept / acknowledge a change, a device to verify, setup failures, sending paused, voice not encrypted): the plain
// "Standard Server" / "Encrypted channel" status is shown by the badge under the server name instead
// (ServerEncryptionBadge), so the chat keeps that space. The lock only appears when this device has set up the
// channel's encrypted session; the server's "Encrypted Server" policy alone never shows one.
import React, { useState } from 'react';
import { requestDeviceVerification } from './cryptoSession';
import './e2ee.css';

function openPrivacySettings() {
  try {
    window.__voxPendingSettingsTab = 'privacy';
    window.dispatchEvent(new CustomEvent('vox-open-settings', { detail: { tab: 'privacy' } }));
  } catch {}
}

export function describeChannelEncryption({ channelKind, serverOn, pending, active, local }) {
  if (pending && pending.action === 'enabled') {
    return { tone: 'warning', icon: '🔐', label: 'Accept encryption', text: 'This server turned on end-to-end encryption. Accept it to keep posting in its text channels.', action: 'accept' };
  }
  if (pending && pending.action === 'disabled') {
    return {
      tone: 'warning',
      icon: '⚠️',
      label: 'Encryption turned off',
      text: 'Important: this server turned off end-to-end encryption. New messages will not be end-to-end encrypted. Earlier encrypted messages stay encrypted. Acknowledge to continue.',
      action: 'ack',
    };
  }
  if (!serverOn) return { tone: 'standard', icon: '🔓', label: 'Standard Server', text: 'Messages in this server are not end-to-end encrypted.' };
  if (channelKind !== 'text') {
    return { tone: 'warning', icon: '🔓', label: 'Not encrypted', text: 'Voice channels, calls and voice-channel chat are not end-to-end encrypted, even on an Encrypted Server.' };
  }
  const st = (local && local.state) || 'none';
  if (active && st === 'ready') {
    return { tone: 'secure', icon: '🔒', label: 'Encrypted channel', text: 'End-to-end encryption is active. Only members’ authorised devices can read new messages. Attachments, stickers and edits are not available here.' };
  }
  if (st === 'unsupported') return { tone: 'warning', icon: '⚠️', label: 'Encryption unavailable here', text: 'This browser cannot run end-to-end encryption, so you cannot read or send messages in this channel here.' };
  if (st === 'failed' && (local.code === 'device_unverified' || local.code === 'identity_missing' || local.code === 'device_removed')) {
    return {
      tone: 'warning',
      icon: '🔑',
      label: 'This device is not verified',
      text: 'You can browse this server, but this browser cannot read or send its encrypted messages until you verify it with your recovery key.',
      action: 'verify',
    };
  }
  if (st === 'failed' && local.code === 'identity_changed') {
    return { tone: 'warning', icon: '⚠️', label: 'A member’s identity changed', text: 'A member’s encryption identity changed. Nothing is sent until the change is reviewed (in a direct message with them).', retry: true };
  }
  if (st === 'failed') {
    return { tone: 'warning', icon: '⚠️', label: 'Encryption setup failed', text: `Encryption could not be set up for this channel on this device${local.error ? ` (${local.error})` : ''}. Sending is paused: nothing will be sent unencrypted.`, retry: true };
  }
  return { tone: 'standard', icon: '⏳', label: 'Setting up encryption', text: 'This device is setting up the channel’s encrypted session; sending is paused until it is ready.' };
}

// A plain status line with nothing to do and nothing to warn about.
export function isStatusOnly(d) {
  if (!d || d.action || d.retry || d.tone === 'warning') return false;
  return d.tone === 'secure' || d.label === 'Standard Server';
}

export default function ChannelEncryptionBar({ channelKind, e2ee }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!e2ee || !e2ee.server) return null;
  const d = describeChannelEncryption({ channelKind, serverOn: e2ee.serverOn, pending: e2ee.pending, active: e2ee.active, local: e2ee.local });
  if (isStatusOnly(d)) return null;
  const showText = open || d.tone === 'warning';
  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(String((e && e.data && e.data.error) || (e && e.message) || e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="vx-e2ee-wrap" data-testid="channel-e2ee">
      <div className={`vx-e2ee-bar is-${d.tone}`} data-e2ee-tone={d.tone} data-e2ee-label={d.label}>
        <span className="vx-e2ee-icon" aria-hidden="true">{d.icon}</span>
        <button type="button" className="vx-e2ee-label" aria-expanded={showText} onClick={() => setOpen((v) => !v)}>
          {d.label}
        </button>
        {showText ? (
          <span className="vx-e2ee-text" data-testid="channel-e2ee-text">
            {d.text}{' '}
            {d.retry ? (
              <button type="button" className="vx-e2ee-link" onClick={e2ee.retry}>
                Try again
              </button>
            ) : null}
            {d.action === 'settings' ? (
              <button type="button" className="vx-e2ee-link" onClick={openPrivacySettings}>
                Encryption settings
              </button>
            ) : null}
            {d.action === 'verify' ? (
              <button type="button" className="vx-e2ee-link" onClick={() => requestDeviceVerification()} data-testid="channel-e2ee-verify">
                Verify this device
              </button>
            ) : null}
          </span>
        ) : null}
        {d.action === 'accept' || d.action === 'ack' ? (
          <button type="button" className="vx-btn small" disabled={busy} onClick={() => run(e2ee.acknowledge)} data-testid="channel-e2ee-ack">
            {busy ? 'Working…' : d.action === 'accept' ? 'Review and accept' : 'Acknowledge'}
          </button>
        ) : null}
      </div>
      {error ? <span className="vx-e2ee-error">{error}</span> : null}
    </div>
  );
}
